// src/pages/public/ComoFunciona.jsx
// Página /como-funciona — explicación del motor cuantitativo Edge Elite.
// Replica 1:1 del mockup `dashboard/edge-elite/edge_elite_como_funciona_v2.html`
// adaptado a React: 6 secciones (hero + flujo + 4 capas + 6 categorías +
// glosario + FAQ con toggle) + aviso legal.
//
// Layout idéntico al ClientDashboard/AdminDashboard: Sidebar + Topbar + main.
// Reusa CSS vars existentes (--accent, --ink, --muted, --bg-elev, --v-enter,
// --v-watch, --neg, --pos, --bg-subtle, --line, --line-strong, --radius,
// --font-display). NO importa Google Fonts ni redefine tipografía.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Sidebar from '../../components/Sidebar';
import Topbar from '../../components/Topbar';
import { useAuth } from '../../auth/AuthContext';
import { ref, onValue } from 'firebase/database';
import { db } from '../../firebase';
import '../../index.css';
import { DashboardThemeProvider } from '../../theme/DashboardTheme';

// ─── Sub-componente: contenido de la página (sin layout) ───────────────────
function FaqItem({ question, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      style={{
        background: 'var(--bg-elev)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        overflow: 'hidden',
        transition: 'border-color .15s',
      }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="cf-faq-button"
        style={{
          width: '100%',
          padding: '18px 20px',
          minHeight: 56,
          fontFamily: 'var(--font-display, inherit)',
          fontSize: 15,
          fontWeight: 600,
          color: 'var(--ink)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'transparent',
          border: 0,
          textAlign: 'left',
          letterSpacing: '-0.01em',
          lineHeight: 1.3,
        }}
        aria-expanded={open}
      >
        <span>{question}</span>
        <span
          aria-hidden="true"
          style={{
            color: 'var(--accent)',
            fontSize: 22,
            fontWeight: 400,
            flexShrink: 0,
            marginLeft: 12,
          }}
        >
          {open ? '−' : '+'}
        </span>
      </button>
      <div
        className="cf-faq-body"
        data-open={open ? 'true' : 'false'}
        style={{
          padding: open ? '0 20px 18px' : '0 20px',
          maxHeight: open ? 500 : 0,
          overflow: 'hidden',
          transition: 'all .3s',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function FlowStep({ icon, title, desc, tone }) {
  const bg =
    tone === 'success' ? 'var(--v-enter, #16A34A)' :
    tone === 'active'  ? 'var(--ink)' :
    'var(--bg-subtle)';
  const color = tone ? '#fff' : 'var(--ink)';
  return (
    <div style={{ flex: 1, textAlign: 'center', padding: 12 }}>
      <div
        style={{
          width: 56, height: 56, margin: '0 auto 12px',
          borderRadius: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24,
          background: bg,
          color,
        }}
      >
        {icon}
      </div>
      <div style={{
        fontFamily: 'var(--font-display, inherit)',
        fontSize: 14,
        fontWeight: 700,
        color: 'var(--ink)',
        marginBottom: 4,
        letterSpacing: '-0.01em',
      }}>
        {title}
      </div>
      <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4 }}>
        {desc}
      </div>
    </div>
  );
}

function FlowArrow() {
  return (
    <span
      aria-hidden="true"
      className="cf-flow-arrow"
      style={{ color: 'var(--line-strong)', fontSize: 20, flexShrink: 0, padding: '0 8px' }}
    >
      →
    </span>
  );
}

function LayerCard({ n, title, desc, metaIcon, metaText }) {
  return (
    <div
      className="cf-layer-card"
      style={{
        background: 'var(--bg-elev)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        padding: 24,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', top: 0, left: 0,
          width: 3, height: '100%',
          background: 'var(--accent)',
        }}
      />
      <div
        style={{
          display: 'inline-block',
          width: 32, height: 32,
          background: 'var(--ink)',
          color: 'var(--bg-elev, #fff)',
          borderRadius: 8,
          fontFamily: 'var(--font-display, inherit)',
          fontSize: 14,
          fontWeight: 800,
          textAlign: 'center',
          lineHeight: '32px',
          marginBottom: 14,
          letterSpacing: '-0.02em',
        }}
      >
        {n}
      </div>
      <h3 style={{
        fontFamily: 'var(--font-display, inherit)',
        fontSize: 18,
        fontWeight: 700,
        color: 'var(--ink)',
        letterSpacing: '-0.02em',
        marginBottom: 8,
        margin: 0,
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: 13,
        color: 'var(--muted)',
        lineHeight: 1.55,
        marginBottom: 16,
        marginTop: 8,
      }}>
        {desc}
      </p>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          paddingTop: 14,
          borderTop: '1px solid var(--line)',
        }}
      >
        <div
          style={{
            width: 28, height: 28,
            background: 'var(--bg-subtle)',
            borderRadius: 7,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          {metaIcon}
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4, flex: 1 }}>
          {metaText}
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ badge, badgeStyle, title, desc }) {
  return (
    <div
      className="cf-category-row"
      style={{
        background: 'var(--bg-elev)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        padding: 18,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
      }}
    >
      <span
        style={{
          flexShrink: 0,
          padding: '5px 12px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          whiteSpace: 'nowrap',
          ...badgeStyle,
        }}
      >
        {badge}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14,
          fontWeight: 700,
          color: 'var(--ink)',
          marginBottom: 4,
          fontFamily: 'var(--font-display, inherit)',
          letterSpacing: '-0.01em',
        }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
          {desc}
        </div>
      </div>
    </div>
  );
}

function GlossaryItem({ term, definition }) {
  return (
    <div
      className="cf-glossary-item"
      style={{
        background: 'var(--bg-elev)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        padding: 18,
      }}
    >
      <div style={{
        fontFamily: 'var(--font-display, inherit)',
        fontSize: 14,
        fontWeight: 700,
        color: 'var(--ink)',
        marginBottom: 6,
        letterSpacing: '-0.01em',
      }}>
        {term}
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
        {definition}
      </div>
    </div>
  );
}

function ComoFuncionaContent() {
  // Estilos de badges de categorías (replica 1:1 del mockup)
  const badgeStyles = {
    entrar:    { background: 'var(--v-enter, #16A34A)', color: '#fff' },
    vigilar:   { background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A' },
    soloInfo:  { background: '#DBEAFE', color: '#1E40AF', border: '1px solid #BFDBFE' },
    esperar:   { background: 'var(--bg-subtle)', color: 'var(--muted)' },
    temprano:  { background: 'var(--bg)', color: 'var(--muted-2)' },
    descartado:{ background: '#FEE2E2', color: 'var(--neg, #DC2626)' },
  };

  return (
    <div className="page cf-page" style={{ padding: '32px 28px 60px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Estilos responsive locales para esta página (mobile-first overrides
          sobre los inline-styles desktop). Ámbito limitado: clases con prefijo cf-. */}
      <style>{`
        @media (max-width: 768px) {
          .cf-page    { padding: 16px 14px 40px !important; }
          .cf-hero    { padding: 24px !important; margin-bottom: 28px !important; }
          .cf-hero h1 { font-size: 32px !important; line-height: 1.05 !important; }
          .cf-hero p  { font-size: 14px !important; }
          .cf-hero-ring  { display: none; }
          .cf-hero-glow  { width: 200px !important; height: 200px !important; top: -60px !important; right: -60px !important; }
          .cf-section { margin-bottom: 32px !important; }
          .cf-section h2     { font-size: 22px !important; }
          .cf-section p      { font-size: 14px !important; }
          .cf-flow-card      { padding: 20px 14px !important; overflow-x: visible !important; }
          .cf-flow-row       { flex-direction: column !important; min-width: 0 !important; }
          .cf-flow-arrow     { transform: rotate(90deg); padding: 4px 0 !important; }
          .cf-grid-2         { grid-template-columns: 1fr !important; }
          .cf-layer-card     { padding: 18px !important; }
          .cf-category-row   { padding: 14px !important; gap: 10px !important; }
          .cf-glossary-item  { padding: 14px !important; }
          .cf-faq-button     { padding: 14px 16px !important; font-size: 14px !important; }
          .cf-faq-body[data-open="true"]  { padding: 0 16px 14px !important; }
          .cf-faq-body[data-open="false"] { padding: 0 16px !important; }
          .cf-legal          { padding: 16px 18px !important; margin-top: 32px !important; }
        }
        @media (max-width: 380px) {
          .cf-hero h1 { font-size: 28px !important; }
        }
      `}</style>
      {/* HERO */}
      <section
        className="cf-hero"
        style={{
          background: 'linear-gradient(180deg, #FF6B2C 0%, #E84B00 60%, #BA3600 100%)',
          borderRadius: 16,
          padding: 40,
          marginBottom: 40,
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden="true"
          className="cf-hero-glow"
          style={{
            position: 'absolute', top: -100, right: -80,
            width: 360, height: 360,
            background: 'radial-gradient(circle, rgba(255,255,255,0.15), transparent 70%)',
            filter: 'blur(20px)',
            pointerEvents: 'none',
          }}
        />
        <div
          aria-hidden="true"
          className="cf-hero-ring"
          style={{
            position: 'absolute', right: -60, bottom: -40,
            width: 220, height: 220,
            borderRadius: '50%',
            border: '14px solid rgba(255,255,255,0.08)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              padding: '6px 12px',
              borderRadius: 999,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              border: '1px solid rgba(255,255,255,0.2)',
              marginBottom: 20,
            }}
          >
            <span
              style={{
                width: 6, height: 6,
                background: '#fff',
                borderRadius: '50%',
                animation: 'pulse 1.8s infinite',
              }}
            />
            Motor cuantitativo activo
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display, inherit)',
            fontSize: 48,
            fontWeight: 800,
            color: '#fff',
            letterSpacing: '-0.04em',
            lineHeight: 0.98,
            marginBottom: 14,
            margin: 0,
          }}>
            Cómo funciona el{' '}
            <span style={{ position: 'relative', display: 'inline-block', color: '#fff' }}>
              motor
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute', left: 0, right: 0, bottom: 4, height: 6,
                  background: 'rgba(255,255,255,0.25)',
                  zIndex: -1,
                  borderRadius: 3,
                }}
              />
            </span>{' '}
            Edge Elite
          </h1>
          <p style={{
            fontSize: 16,
            color: 'rgba(255,255,255,0.9)',
            lineHeight: 1.5,
            maxWidth: 700,
            marginTop: 14,
            margin: '14px 0 0 0',
          }}>
            El motor cuantitativo Edge Elite analiza partidos de básquetbol en tiempo real
            para identificar oportunidades estadísticamente significativas en el mercado
            Over/Under. Cada decisión está fundamentada en metodología cuantitativa rigurosa
            y validada sobre cientos de partidos históricos.
          </p>
        </div>
      </section>

      {/* SECCIÓN 1: Flujo del motor */}
      <section className="cf-section" style={{ marginBottom: 48 }}>
        <span style={{
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--muted)',
          display: 'inline-block',
          marginBottom: 12,
        }}>El proceso</span>
        <h2 style={{
          fontFamily: 'var(--font-display, inherit)',
          fontSize: 28,
          fontWeight: 700,
          color: 'var(--ink)',
          letterSpacing: '-0.025em',
          lineHeight: 1.1,
          marginBottom: 12,
          margin: 0,
        }}>
          Cómo procesa cada partido
        </h2>
        <p style={{
          fontSize: 15,
          color: 'var(--muted)',
          lineHeight: 1.55,
          maxWidth: 700,
          marginBottom: 24,
          marginTop: 12,
        }}>
          El motor opera en un flujo de cuatro etapas, ejecutándose continuamente sobre
          cada partido en vivo. En milisegundos, evalúa múltiples capas de información
          antes de emitir un veredicto.
        </p>
        <div
          className="cf-flow-card"
          style={{
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            borderRadius: 14,
            padding: '32px 24px',
            overflowX: 'auto',
          }}
        >
          <div className="cf-flow-row" style={{ display: 'flex', alignItems: 'center', minWidth: 700 }}>
            <FlowStep icon="📡" title="Captura" desc="Datos en tiempo real del partido" tone="active" />
            <FlowArrow />
            <FlowStep icon="⚙️" title="Análisis" desc="Procesamiento cuantitativo" tone="active" />
            <FlowArrow />
            <FlowStep icon="🔍" title="Validación" desc="Cuatro capas de filtrado" tone="active" />
            <FlowArrow />
            <FlowStep icon="⚡" title="Veredicto" desc="Recomendación final" tone="success" />
          </div>
        </div>
      </section>

      {/* SECCIÓN 2: Las 4 capas */}
      <section className="cf-section" style={{ marginBottom: 48 }}>
        <span style={{
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--muted)',
          display: 'inline-block',
          marginBottom: 12,
        }}>Validaciones</span>
        <h2 style={{
          fontFamily: 'var(--font-display, inherit)',
          fontSize: 28,
          fontWeight: 700,
          color: 'var(--ink)',
          letterSpacing: '-0.025em',
          lineHeight: 1.1,
          marginBottom: 12,
          margin: 0,
        }}>
          Las cuatro capas del motor
        </h2>
        <p style={{
          fontSize: 15,
          color: 'var(--muted)',
          lineHeight: 1.55,
          maxWidth: 700,
          marginBottom: 24,
          marginTop: 12,
        }}>
          Para que un partido reciba la recomendación{' '}
          <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>Entrar</strong>, debe
          superar cuatro validaciones independientes. Cada capa filtra señales que
          históricamente no han mostrado ventaja estadística sostenible.
        </p>
        <div
          className="layers-grid cf-grid-2"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 16,
          }}
        >
          <LayerCard
            n="1"
            title="Selección de liga"
            desc="El motor solo opera en ligas donde tenemos evidencia estadística sólida sobre cientos de partidos. No todas las ligas se prestan a un enfoque cuantitativo: algunas presentan alta volatilidad, mercados poco eficientes o falta de data confiable."
            metaIcon="🏀"
            metaText={<>Cobertura validada en <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>ligas latinoamericanas y europeas</strong> con histórico estable.</>}
          />
          <LayerCard
            n="2"
            title="Ventana temporal óptima"
            desc="Identificamos momentos específicos del partido donde las líneas del bookmaker tienden a estar mispriced. Ciertos puntos del juego ofrecen ventaja estadística superior, mientras que otros destruyen el ROI consistentemente."
            metaIcon="⏱️"
            metaText={<>Solo intervenimos en <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>fases del partido validadas</strong> mediante backtest.</>}
          />
          <LayerCard
            n="3"
            title="Magnitud del edge"
            desc="Requerimos un margen mínimo entre nuestra proyección cuantitativa y la línea del mercado. Sin este margen, el costo de la apuesta anula cualquier ventaja estadística. Los picks con edge insuficiente se descartan automáticamente."
            metaIcon="📐"
            metaText="Filtra señales débiles que no superan el costo de la apuesta."
          />
          <LayerCard
            n="4"
            title="Validación direccional"
            desc="Algunas ligas muestran sesgos sistemáticos en una dirección (Over o Under). Aplicamos filtros direccionales específicos por liga basados en análisis histórico extensivo. Esto evita apostar contra patrones documentados."
            metaIcon="🎯"
            metaText={<>Respeta los <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>sesgos estadísticos</strong> documentados por liga.</>}
          />
        </div>
      </section>

      {/* SECCIÓN 3: Las 6 categorías */}
      <section className="cf-section" style={{ marginBottom: 48 }}>
        <span style={{
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--muted)',
          display: 'inline-block',
          marginBottom: 12,
        }}>Veredictos</span>
        <h2 style={{
          fontFamily: 'var(--font-display, inherit)',
          fontSize: 28,
          fontWeight: 700,
          color: 'var(--ink)',
          letterSpacing: '-0.025em',
          lineHeight: 1.1,
          marginBottom: 12,
          margin: 0,
        }}>
          Las seis categorías del motor
        </h2>
        <p style={{
          fontSize: 15,
          color: 'var(--muted)',
          lineHeight: 1.55,
          maxWidth: 700,
          marginBottom: 24,
          marginTop: 12,
        }}>
          Cada partido recibe uno de seis veredictos posibles. Solo los marcados como{' '}
          <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>Entrar</strong> son
          recomendados para apostar. El resto se muestra con fines informativos para
          que puedas seguir todos los partidos en vivo.
        </p>
        <div
          className="cf-grid-2"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 12,
          }}
        >
          <CategoryRow badge="⚡ Entrar" badgeStyle={badgeStyles.entrar}
            title="Pick recomendado"
            desc="Pasó las cuatro validaciones del motor. Coincide con lo enviado al canal Telegram." />
          <CategoryRow badge="Vigilar" badgeStyle={badgeStyles.vigilar}
            title="En seguimiento activo"
            desc="El motor está siguiendo este partido. Puede activarse a Entrar si las condiciones cambian." />
          <CategoryRow badge="Esperar" badgeStyle={badgeStyles.esperar}
            title="Aún sin condiciones óptimas"
            desc="Liga válida pero el partido aún no entra a la ventana donde el motor opera." />
          <CategoryRow badge="Solo info" badgeStyle={badgeStyles.soloInfo}
            title="No recomendado por motor"
            desc="La proyección no cumple con los criterios direccionales del motor para esta liga." />
          <CategoryRow badge="Temprano" badgeStyle={badgeStyles.temprano}
            title="Sin data suficiente"
            desc="Partido recién iniciado o pre-partido. El motor espera acumular información." />
          <CategoryRow badge="Descartado" badgeStyle={badgeStyles.descartado}
            title="Fuera de cobertura del motor"
            desc="Liga donde el motor no opera por no cumplir requisitos de validación estadística." />
        </div>
      </section>

      {/* SECCIÓN 4: Glosario */}
      <section className="cf-section" style={{ marginBottom: 48 }}>
        <span style={{
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--muted)',
          display: 'inline-block',
          marginBottom: 12,
        }}>Conceptos</span>
        <h2 style={{
          fontFamily: 'var(--font-display, inherit)',
          fontSize: 28,
          fontWeight: 700,
          color: 'var(--ink)',
          letterSpacing: '-0.025em',
          lineHeight: 1.1,
          marginBottom: 12,
          margin: 0,
        }}>
          Glosario rápido
        </h2>
        <p style={{
          fontSize: 15,
          color: 'var(--muted)',
          lineHeight: 1.55,
          maxWidth: 700,
          marginBottom: 24,
          marginTop: 12,
        }}>
          Términos que aparecen frecuentemente en el dashboard y en el canal de señales.
        </p>
        <div
          className="cf-grid-2"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 12,
          }}
        >
          <GlossaryItem term="Línea O/U"
            definition="Total de puntos establecido por el bookmaker. Apostás si el partido terminará por encima (Over) o por debajo (Under) de ese número." />
          <GlossaryItem term="Edge"
            definition="Diferencia entre nuestra proyección cuantitativa y la línea del bookmaker. Mientras mayor el edge, mayor la divergencia entre el modelo y el mercado." />
          <GlossaryItem term="Confianza del motor"
            definition="Indicador de la solidez estadística de un veredicto. Considera múltiples factores internos del motor antes de mostrar el porcentaje." />
          <GlossaryItem term="Pace"
            definition="Ritmo del partido medido en posesiones por 48 minutos. Es uno de los factores principales que determina el total esperado." />
          <GlossaryItem term="Bankroll management"
            definition="Disciplina de gestión del capital. Recomendamos apostar entre 1% y 3% del bankroll por pick, nunca más, para reducir varianza." />
          <GlossaryItem term="Veredicto"
            definition="Decisión final del motor sobre cada partido. Una de las seis categorías: Entrar, Vigilar, Esperar, Solo info, Temprano o Descartado." />
        </div>
      </section>

      {/* SECCIÓN 5: FAQ */}
      <section className="cf-section" style={{ marginBottom: 48 }}>
        <span style={{
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--muted)',
          display: 'inline-block',
          marginBottom: 12,
        }}>Preguntas frecuentes</span>
        <h2 style={{
          fontFamily: 'var(--font-display, inherit)',
          fontSize: 28,
          fontWeight: 700,
          color: 'var(--ink)',
          letterSpacing: '-0.025em',
          lineHeight: 1.1,
          marginBottom: 24,
          margin: '0 0 24px',
        }}>
          Lo que más nos preguntan
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <FaqItem question="¿Por qué algunos días hay muchos picks y otros pocos?" defaultOpen>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
              El motor solo recomienda apostar cuando se cumplen las cuatro validaciones simultáneamente.
              Esto depende del calendario de las ligas que cubrimos, las condiciones específicas de cada
              partido y los movimientos del mercado.
            </p>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginTop: 10 }}>
              Hay días donde se alinean varias oportunidades y otros donde el motor no detecta ninguna
              con suficiente confianza. Esto es normal y esperado:{' '}
              <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>preferimos no recomendar a recomendar mal</strong>.
            </p>
          </FaqItem>
          <FaqItem question="¿Cuánto debería apostar por pick?">
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
              Recomendamos no más del 1% al 3% de tu bankroll por pick. Apostar más expone a varianza
              estadística que puede generar pérdidas importantes incluso con un modelo profitable.
            </p>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginTop: 10 }}>
              La disciplina del usuario es tan importante como la calidad del modelo. Un usuario
              indisciplinado pierde con cualquier sistema.
            </p>
          </FaqItem>
          <FaqItem question="¿El motor garantiza que voy a ganar dinero?">
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>No.</strong> Edge Elite es una
              plataforma de análisis estadístico, no un servicio de apuestas garantizadas. El motor
              identifica oportunidades con ventaja estadística histórica, pero el resultado de cada
              partido individual es incierto.
            </p>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginTop: 10 }}>
              La ventaja del modelo se manifiesta en el largo plazo (cientos de picks), no en cualquier
              pick individual. Apostadores disciplinados que siguen el sistema con bankroll management
              apropiado tienen mejor probabilidad de obtener resultados positivos sostenibles.
            </p>
          </FaqItem>
          <FaqItem question="¿Por qué hay ligas que el motor no cubre?">
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
              El motor solo opera en ligas donde ha demostrado ventaja estadística significativa sobre
              backtest histórico. Algunas ligas presentan características que dificultan el modelado
              cuantitativo: alta volatilidad, mercados muy eficientes, calendarios irregulares o falta
              de data confiable.
            </p>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginTop: 10 }}>
              Antes que recomendar en ligas donde no tenemos validación, preferimos limitar la cobertura
              a las ligas donde el motor ha demostrado funcionar consistentemente.
            </p>
          </FaqItem>
          <FaqItem question="¿Qué pasa si pierdo varios picks seguidos?">
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
              Las rachas de pérdidas son normales y esperables incluso con modelos profitables. La
              varianza estadística se manifiesta naturalmente: un modelo con 65% de accuracy puede
              tener rachas de 5-7 pérdidas consecutivas y seguir siendo profitable a largo plazo.
            </p>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginTop: 10 }}>
              Lo importante es seguir la disciplina del bankroll management: nunca aumentes el stake
              tras una pérdida buscando recuperar, ni lo reduzcas dramáticamente por miedo. Mantené el
              mismo porcentaje de bankroll por pick.
            </p>
          </FaqItem>
          <FaqItem question="¿Cómo se mejora el motor con el tiempo?">
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
              El motor se actualiza periódicamente con base en data nueva. Cada partido procesado
              genera información que alimenta análisis futuros. Versiones nuevas del motor incorporan
              mejoras basadas en patrones detectados durante operación real.
            </p>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginTop: 10 }}>
              La iteración continua es parte del enfoque científico: ningún modelo predictivo es
              perfecto desde el día uno, y la ventaja sostenible viene de la capacidad de mejorar
              constantemente.
            </p>
          </FaqItem>
        </div>
      </section>

      {/* AVISO LEGAL */}
      <div
        className="cf-legal"
        style={{
          marginTop: 60,
          padding: '22px 24px',
          background: 'var(--bg-subtle)',
          borderRadius: 14,
          borderLeft: '3px solid var(--muted)',
        }}
      >
        <div style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--ink-2, var(--ink))',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: 8,
        }}>
          Aviso legal
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>
          Edge Elite es una plataforma de análisis estadístico para el mercado Over/Under en básquetbol.
          No somos un operador de apuestas. La información proporcionada es para fines analíticos e
          informativos. El usuario es responsable de sus decisiones de apuesta. Las apuestas conllevan
          riesgo de pérdida económica. No recomendamos apostar dinero que no estés dispuesto a perder.
          Si crees que tu actividad de apuestas se ha vuelto problemática, busca ayuda profesional.
        </div>
      </div>
    </div>
  );
}

// ─── Wrapper con layout (Sidebar + Topbar) ────────────────────────────────
export default function ComoFunciona() {
  const navigate = useNavigate();
  const { user: authUser, userData, isAdmin } = useAuth();

  // Estado mínimo para que Sidebar/Topbar funcionen (mismo patrón que
  // ClientDashboard / AdminDashboard pero sin loops de picks).
  const [picks, setPicks] = useState([]);
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const user = {
    uid: authUser?.uid || null,
    displayName: userData?.nombre || authUser?.displayName || 'Cliente',
    email: authUser?.email || null,
  };

  // Listener mínimo a /picks solo para que Sidebar muestre las ligas en vivo
  // (igual que en los Dashboards). Sin esto, "Todos" mostraría 0.
  useEffect(() => {
    const picksRef = ref(db, 'picks');
    const unsub = onValue(picksRef, snap => {
      const data = snap.val();
      if (!data) { setConnected(false); setPicks([]); return; }
      const list = Object.entries(data).map(([id, val]) => ({ id, ...val }));
      setPicks(list);
      setConnected(true);
      if (list[0]?.timestamp) setLastUpdate(new Date(list[0].timestamp));
    }, () => setConnected(false));
    return () => unsub();
  }, []);

  function getAgeStr() {
    if (!lastUpdate) return '—';
    const secs = Math.floor((Date.now() - lastUpdate.getTime()) / 1000);
    if (secs < 10) return 'ahora';
    if (secs < 60) return `hace ${secs}s`;
    return `hace ${Math.floor(secs / 60)}m`;
  }
  function getAgeColor() {
    if (!lastUpdate) return 'var(--muted)';
    const secs = Math.floor((Date.now() - lastUpdate.getTime()) / 1000);
    if (secs < 15) return 'var(--pos)';
    if (secs < 30) return 'var(--warn)';
    return 'var(--neg)';
  }

  // Sidebar handler: si el user clickea cualquier item del menú normal,
  // navegamos al dashboard correspondiente con la página seleccionada
  // persistida en localStorage (que ClientDashboard/AdminDashboard leen al
  // montar). El item "Cómo funciona" se maneja como link especial dentro
  // del Sidebar (ver Sidebar.jsx).
  function handleNav(p, league) {
    if (league) localStorage.setItem('ee_league', league);
    if (p) localStorage.setItem('ee_route', p);
    navigate(isAdmin ? '/admin/dashboard' : '/dashboard');
    setSidebarOpen(false);
  }

  return (
    <DashboardThemeProvider>
    <div className="app-layout app dashboard-scoped">
      <Helmet>
        <title>Cómo funciona · Edge Elite</title>
      </Helmet>
      <Sidebar
        page="como-funciona"
        setPage={(p) => handleNav(p)}
        selectedLeague="all"
        setLeague={(l) => handleNav('live', l)}
        picks={picks}
        user={user}
        userData={userData}
        isAdmin={isAdmin}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="main-content main">
        <Topbar
          connected={connected}
          lastUpdate={lastUpdate}
          getAgeStr={getAgeStr}
          getAgeColor={getAgeColor}
          onMenuOpen={() => setSidebarOpen(true)}
          picks={picks}
        />
        <ComoFuncionaContent />
      </div>
    </div>
    </DashboardThemeProvider>
  );
}
